/* SKIN QUIZ controller. Spec: channels/shopify/skin-quiz-2026-09/BUILD-HANDOFF.md
 *
 * Owns: screen state, question rendering, keyboard, focus.
 * Doesn't own: which products to show, safety rules, prices. Those all come
 * from SkinQuizEngine (assets/skin-quiz-engine.js), which is Node-tested.
 *
 * Build status (phases in the handoff):
 * - Phase 2: intro → Q1–Q6 → safety question, keyboard, a11y.
 * - Phase 3: result page, swap drawer/sheet, sticky bar, resume, share URL.
 * - Phase 4: add to bag (line properties + cart attributes).
 * - Phase 5: track() with the debug / GA4 / Clarity adapters. The event-log
 *   adapter (batched beacons) arrives with the Worker in phase 6.
 * - Phase 7 Klaviyo is still a stub: the email card collects an address and
 *   shows the sent state, but sends nothing.
 *
 * Privacy: `pregnant` lives only in `state.answers` in memory. Anything that
 * leaves the page (storage, URL, analytics) goes through engine.stripPrivate.
 *
 * Keyboard (desktop):
 * - A–F picks an answer. Picking moves on (Q2 waits for Continue).
 * - Arrow keys move focus between answers; Space/Enter picks. Arrows move focus
 *   only, not the selection, because a selection advances the quiz.
 * - Backspace, or ← when focus isn't on an answer, goes back.
 */
(function () {
  'use strict';

  var ADVANCE_MS = 150;
  var SLOTS = ['cleanse', 'treat_am', 'treat_pm', 'moisturize', 'spf'];
  var LETTERS = 'ABCDEFGH'; // enough for the longest answer list

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function readJson(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k) || attrs[k] == null || attrs[k] === false) continue;
        if (k === 'class') node.className = attrs[k];
        else if (k === 'text') node.textContent = attrs[k];
        else node.setAttribute(k, attrs[k] === true ? '' : attrs[k]);
      }
    }
    (children || []).forEach(function (c) { if (c) node.appendChild(c); });
    return node;
  }

  function fill(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : m;
    });
  }

  function money(cents) {
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  // A SKU hue softened toward white, the way the design system builds its tints.
  function tint(hex, amount) {
    if (!hex) return 'transparent';
    return 'color-mix(in srgb, ' + hex + ' ' + Math.round(amount * 100) + '%, #fff)';
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function SkinQuiz(root) {
    this.root = root;
    this.engine = window.SkinQuizEngine;
    this.config = readJson(document.getElementById('skin-quiz-config'));
    this.settings = readJson(root.querySelector('[data-sq-settings]')) || {};
    this.introEl = root.querySelector('[data-sq-intro]');
    this.stepEl = root.querySelector('[data-sq-step]');
    this.backEl = root.querySelector('[data-sq-back]');
    this.progressEl = root.querySelector('[data-sq-progress]');
    this.statusEl = root.querySelector('[data-sq-status]');
    this.state = {
      screen: 'intro', index: -1, answers: {}, swaps: {}, removed: [], editing: false,
      startedAt: 0, stepStartedAt: 0, added: false, restored: false, howTo: 'am'
    };
    this.advanceTimer = null;
    this.swapEl = null;
    this.stickyEl = null;
    this.stickyObserver = null;
  }

  SkinQuiz.prototype.init = function () {
    if (!this.engine || !this.config) {
      // Fail visibly in the console, quietly on the page: the intro stays.
      console.error('[skin-quiz] engine or config missing');
      return;
    }
    var self = this;
    this.ui = this.config.ui || {};
    this.root.querySelector('[data-sq-start]').addEventListener('click', function () { self.start(); });
    this.backEl.addEventListener('click', function () { self.back(); });
    this.retakeEl = this.root.querySelector('[data-sq-retake]');
    this.shareEl = this.root.querySelector('[data-sq-share]');
    this.resumeEl = this.root.querySelector('[data-sq-resume]');
    if (this.retakeEl) this.retakeEl.addEventListener('click', function () { self.retake(); });
    if (this.shareEl) this.shareEl.addEventListener('click', function () { self.share(); });
    document.addEventListener('keydown', function (e) { self.onKeydown(e); });
    // iOS Safari doesn't reliably fire unload; visibilitychange is the one that lands.
    var sendAbandon = function () {
      if (self.state.screen === 'intro' || self.state.screen === 'result' || self.abandonSent) return;
      self.abandonSent = true;
      self.track('quiz_abandon', {
        last_step: self.state.index + 1,
        ms_total: self.state.startedAt ? Date.now() - self.state.startedAt : 0
      });
    };
    window.addEventListener('pagehide', sendAbandon);
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') sendAbandon(); });
    this.track('quiz_view', { entry_source: entrySource() });

    // A share link wins over local storage: it's the routine someone was sent.
    var shared = this.readShareUrl();
    var saved = shared || this.load();
    if (shared && this.restore(shared)) {
      this.state.restored = true;
      return this.finish({ silent: true });
    }
    if (saved && this.resumeEl) {
      this.resumeEl.hidden = false;
      this.resumeEl.addEventListener('click', function () {
        if (self.restore(saved)) self.finish({ silent: true });
      });
    }
  };

  // ---------- analytics (phase 5) ----------
  //
  // One track() call per thing that happens; adapters decide where it goes.
  // Every event is filtered against EVENTS below — a prop that isn't on the
  // list never leaves the page, which is how the pregnancy answer stays put
  // (there is no `pregnant` prop anywhere in this table, by design).
  //
  // Types: s = string, n = number, b = boolean. Anything else is dropped.

  // The event table lives in the config (test/fixtures/config.json), so the
  // theme and the Worker validate against the same list and can't drift.

  // Clarity gets the three that matter for watching a session, not all 18.
  var CLARITY_EVENTS = ['quiz_add_all', 'quiz_add_one', 'quiz_email_submit'];

  function entrySource() {
    try {
      var utm = new URLSearchParams(location.search).get('utm_source');
      if (utm) return utm;
      if (document.referrer) {
        var host = new URL(document.referrer).hostname;
        if (host && host !== location.hostname) return host;
      }
    } catch (e) { /* malformed referrer */ }
    return 'direct';
  }

  SkinQuiz.prototype.analyticsKey = function (key) {
    return key === 'pregnant' ? 'safety_check' : key;
  };

  SkinQuiz.prototype.analyticsAllowed = function () {
    // Shopify's consent API. The spec only requires this for the event log,
    // but sending to GA4 or Clarity after someone opts out would be worse.
    try {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.analyticsProcessingAllowed === 'function') return api.analyticsProcessingAllowed() !== false;
    } catch (e) { /* API not loaded */ }
    return true;
  };

  SkinQuiz.prototype.track = function (name, props) {
    var spec = (this.config.events || {})[name];
    if (!spec) {
      console.warn('[skin-quiz] unknown event', name);
      return;
    }
    var clean = {};
    for (var key in spec) {
      if (!props || !Object.prototype.hasOwnProperty.call(props, key)) continue;
      var v = props[key];
      if (v == null || v === '') continue;
      if (spec[key] === 'n') { var n = Number(v); if (!isNaN(n)) clean[key] = n; }
      else if (spec[key] === 'b') clean[key] = !!v;
      else clean[key] = String(v);
    }
    var payload = {
      sid: this.sid(),
      qv: this.config.quiz_version,
      env: this.settings.env || 'test',
      ts: new Date().toISOString(),
      device: window.innerWidth < 750 ? 'mobile' : 'desktop',
      entry_source: this.entrySource || (this.entrySource = entrySource())
    };
    for (var k in clean) payload[k] = clean[k];

    this.debugAdapter(name, payload);
    if (!this.analyticsAllowed()) return;
    this.ga4Adapter(name, payload);
    this.clarityAdapter(name, payload);
    this.logAdapter(name, payload);
  };

  // ---------- event log (our Worker) ----------
  //
  // Batched, so a quiz run is a handful of requests rather than twenty. Sent
  // as text/plain so sendBeacon needs no preflight, and flushed on
  // visibilitychange rather than unload, which iOS Safari doesn't fire.

  SkinQuiz.prototype.endpoint = function () {
    return (this.settings.event_endpoint || '').trim().replace(/\/+$/, '');
  };

  SkinQuiz.prototype.logAdapter = function (name, payload) {
    if (!this.endpoint()) return;
    var event = { e: name };
    for (var k in payload) if (k !== 'sid' && k !== 'qv' && k !== 'env') event[k] = payload[k];
    this.queue = this.queue || [];
    this.queue.push(event);

    var self = this;
    if (!this.flushBound) {
      this.flushBound = true;
      var flush = function () { self.flushLog(); };
      document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(); });
      window.addEventListener('pagehide', flush);
    }
    if (this.queue.length >= 10) return this.flushLog();
    if (!this.flushTimer) this.flushTimer = setTimeout(function () { self.flushLog(); }, 5000);
  };

  SkinQuiz.prototype.flushLog = function () {
    if (this.flushTimer) { clearTimeout(this.flushTimer); this.flushTimer = null; }
    if (!this.queue || !this.queue.length) return;
    var endpoint = this.endpoint();
    if (!endpoint) { this.queue = []; return; }
    var body = JSON.stringify({
      v: 1,
      sid: this.sid(),
      env: this.settings.env || 'test',
      qv: this.config.quiz_version,
      events: this.queue.splice(0, 50)
    });
    var url = endpoint + '/q/e';
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) return;
    } catch (e) { /* beacon refused; fall through to fetch */ }
    try {
      fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'text/plain' },
        body: body, keepalive: true, mode: 'cors'
      }).catch(function () {});
    } catch (e) { /* offline */ }
  };

  SkinQuiz.prototype.ga4Adapter = function (name, payload) {
    if (this.settings.ga4_enabled === false || typeof window.gtag !== 'function') return;
    var params = { quiz_env: payload.env, quiz_version: payload.qv, quiz_session: payload.sid };
    for (var k in payload) {
      if (k === 'sid' || k === 'qv' || k === 'env' || k === 'ts') continue;
      params[k] = payload[k];
    }
    // GTM is commented out in theme.liquid, so this must be gtag, not dataLayer.
    window.gtag('event', name, params);
  };

  SkinQuiz.prototype.clarityAdapter = function (name, payload) {
    if (typeof window.clarity !== 'function') return;
    try {
      if (payload.step != null) window.clarity('set', 'quiz_step', String(payload.step));
      if (payload.routine) window.clarity('set', 'quiz_routine', payload.routine);
      if (CLARITY_EVENTS.indexOf(name) !== -1) window.clarity('event', name);
    } catch (e) { /* Clarity not ready */ }
  };

  // ---------- debug panel (?quizdebug=1) ----------

  SkinQuiz.prototype.debugOn = function () {
    if (this._debug == null) {
      var flag = null;
      try { flag = new URLSearchParams(location.search).get('quizdebug'); } catch (e) { /* no URL API */ }
      this._debug = flag === '1' || (flag !== '0' && this.settings.debug_default === true);
    }
    return this._debug;
  };

  SkinQuiz.prototype.debugAdapter = function (name, payload) {
    if (!this.debugOn()) return;
    if (console && console.table) console.table([Object.assign({ event: name }, payload)]);
    var panel = this.debugPanel || this.buildDebugPanel();
    var rest = {};
    for (var k in payload) if (['sid', 'qv', 'env', 'ts', 'device', 'entry_source'].indexOf(k) === -1) rest[k] = payload[k];
    var row = el('li', null, [
      el('strong', { text: name }),
      el('span', { text: Object.keys(rest).length ? ' ' + JSON.stringify(rest) : '' })
    ]);
    panel.list.appendChild(row);
    panel.list.scrollTop = panel.list.scrollHeight;
    panel.count.textContent = String(panel.list.children.length);
  };

  SkinQuiz.prototype.buildDebugPanel = function () {
    var self = this;
    var list = el('ol', { class: 'sq-debug__list' });
    var count = el('span', { class: 'sq-debug__count', text: '0' });
    var copy = el('button', { type: 'button', class: 'sq-debug__btn', text: 'Copy' });
    copy.addEventListener('click', function () {
      var text = Array.prototype.map.call(list.children, function (li) { return li.textContent; }).join('\n');
      if (navigator.clipboard) navigator.clipboard.writeText(text);
    });
    var hide = el('button', { type: 'button', class: 'sq-debug__btn', text: 'Hide' });
    var box = el('div', { class: 'sq-debug', 'data-sq-debug': true }, [
      el('div', { class: 'sq-debug__head' }, [
        el('strong', { text: 'Quiz events ' }), count,
        el('span', { class: 'sq-debug__env', text: ' · ' + (this.settings.env || 'test') }),
        copy, hide
      ]),
      list
    ]);
    hide.addEventListener('click', function () { box.remove(); self.debugPanel = null; self._debug = false; });
    document.body.appendChild(box);
    this.debugPanel = { box: box, list: list, count: count };
    return this.debugPanel;
  };

  // ---------- navigation ----------

  SkinQuiz.prototype.flow = function () {
    return this.engine.questionFlow(this.state.answers, this.config);
  };

  SkinQuiz.prototype.start = function () {
    this.state.startedAt = Date.now();
    this.track('quiz_start', {});
    // Cart attributes are the only thing that reaches the Shopify order export,
    // so stamp the session on the cart before anyone adds anything.
    this.updateCartAttributes({ quiz_session: this.sid() });
    this.showQuestion(0);
  };

  SkinQuiz.prototype.retake = function () {
    this.track('quiz_retake', {});
    this.state.answers = {};
    this.state.swaps = {};
    this.state.removed = [];
    this.state.editing = false;
    this.state.added = false;
    this.state.restored = false;
    this.clearSaved();
    this.clearShareUrl();
    this.showIntro();
  };

  SkinQuiz.prototype.showQuestion = function (index) {
    var flow = this.flow();
    if (index >= flow.length) return this.finish();
    this.clearAdvance();
    this.state.screen = 'question';
    this.state.index = index;
    this.state.stepStartedAt = Date.now();
    this.renderQuestion(flow[index], index);
    // The safety step reports as `safety_check`: the funnel still counts it,
    // but the word "pregnant" never reaches GA4, Clarity or the event log.
    this.track('quiz_step_view', { step: index + 1, question_key: this.analyticsKey(flow[index].key) });
  };

  SkinQuiz.prototype.next = function () {
    // Editing one answer from the result goes back to the result, not through
    // the rest of the questions — unless the edit left a gap (a changed Q1
    // clears a clashing Q2, a new retinoid needs the safety answer).
    if (this.state.editing) {
      var flow = this.flow();
      for (var i = 0; i < flow.length; i++) {
        if (this.state.answers[flow[i].key] == null) return this.showQuestion(i);
      }
      return this.finish();
    }
    this.showQuestion(this.state.index + 1);
  };

  SkinQuiz.prototype.back = function () {
    this.clearAdvance();
    if (this.state.screen === 'result') return this.showQuestion(this.flow().length - 1);
    if (this.state.screen !== 'question') return;
    this.track('quiz_back', { step: this.state.index + 1 });
    if (this.state.index <= 0) return this.showIntro();
    this.showQuestion(this.state.index - 1);
  };

  SkinQuiz.prototype.jumpTo = function (key) {
    if (this.state.screen === 'result') this.state.editing = true;
    var flow = this.flow();
    for (var i = 0; i < flow.length; i++) if (flow[i].key === key) return this.showQuestion(i);
  };

  SkinQuiz.prototype.showIntro = function () {
    this.state.screen = 'intro';
    this.state.index = -1;
    this.root.setAttribute('data-screen', 'intro');
    this.stepEl.hidden = true;
    this.stepEl.textContent = '';
    this.introEl.hidden = false;
    this.backEl.hidden = true;
    this.progressEl.hidden = true;
    if (this.retakeEl) this.retakeEl.hidden = true;
    if (this.shareEl) this.shareEl.hidden = true;
    if (this.stickyEl) { this.stickyEl.remove(); this.stickyEl = null; }
    if (this.stickyObserver) { this.stickyObserver.disconnect(); this.stickyObserver = null; }
    if (this.resumeEl) this.resumeEl.hidden = !this.load();
    this.announce('');
    var start = this.root.querySelector('[data-sq-start]');
    this.scrollToTop();
    start.focus();
  };

  SkinQuiz.prototype.finish = function (opts) {
    if (!this.engine.isComplete(this.state.answers, this.config)) {
      // Shouldn't happen from the UI; send them to the first gap instead of a broken result.
      var flow = this.flow();
      for (var i = 0; i < flow.length; i++) {
        if (this.state.answers[flow[i].key] == null) return this.showQuestion(i);
      }
    }
    var first = this.state.screen !== 'result';
    this.state.editing = false;
    this.state.screen = 'result';
    var r = this.result();
    if (first && !(opts && opts.silent)) {
      var a = this.state.answers;
      this.track('quiz_complete', {
        routine: r.routine.key, skin_type: a.skin_type, concern_1: a.concern_1, concern_2: a.concern_2,
        reactivity: a.reactivity, age_band: a.age_band, routine_now: a.routine_now,
        safety_swap: r.safetySwap, ms_total: this.state.startedAt ? Date.now() - this.state.startedAt : 0
      });
    }
    this.track('quiz_result_view', {
      routine: r.routine.key, slots_shown: r.slots.length, tier_rate: r.tier ? r.tier.rate : 0
    });
    this.save();
    this.renderResult(r);
    if (first) this.klaviyoTrack(r, 'complete');
  };

  SkinQuiz.prototype.result = function () {
    return this.engine.recommend(this.state.answers, this.config, this.state.swaps, this.state.removed);
  };

  SkinQuiz.prototype.choose = function (question, value) {
    var answers = this.state.answers;
    var changed = answers[question.key] !== value;
    answers[question.key] = value;
    // Q2 can't repeat Q1. If Q1 changes to what Q2 was, Q2 needs a fresh answer.
    if (question.key === 'concern_1' && answers.concern_2 === value) delete answers.concern_2;
    // "Nothing in particular" skips Q2, so drop anything already answered there.
    this.engine.skippedKeys(answers, this.config).forEach(function (key) { delete answers[key]; });

    if (question.key !== 'pregnant') {
      this.track('quiz_answer', {
        step: this.state.index + 1,
        question_key: question.key,
        answer_value: value,
        ms_on_step: Date.now() - this.state.stepStartedAt
      });
    }

    this.syncChecked(value);
    if (question.type === 'optional' && value !== 'none') {
      var cont = this.stepEl.querySelector('[data-sq-continue]');
      if (cont) cont.removeAttribute('aria-disabled');
      return;
    }
    var self = this;
    this.clearAdvance();
    this.advanceTimer = setTimeout(function () { self.next(); }, changed ? ADVANCE_MS : 0);
  };

  SkinQuiz.prototype.clearAdvance = function () {
    if (this.advanceTimer) clearTimeout(this.advanceTimer);
    this.advanceTimer = null;
  };

  // ---------- rendering ----------

  SkinQuiz.prototype.showStep = function (content) {
    this.root.setAttribute('data-screen', this.state.screen);
    if (this.state.screen !== 'result') {
      if (this.retakeEl) this.retakeEl.hidden = true;
      if (this.shareEl) this.shareEl.hidden = true;
      if (this.stickyEl) { this.stickyEl.remove(); this.stickyEl = null; }
      if (this.stickyObserver) { this.stickyObserver.disconnect(); this.stickyObserver = null; }
    }
    this.introEl.hidden = true;
    this.stepEl.textContent = '';
    this.stepEl.appendChild(content);
    this.stepEl.hidden = false;
    this.backEl.hidden = false;
    var stepEl = this.stepEl;
    stepEl.classList.remove('is-entering');
    void stepEl.offsetWidth; // restart the enter animation
    stepEl.classList.add('is-entering');
    this.scrollToTop();
  };

  SkinQuiz.prototype.renderQuestion = function (q, index) {
    var self = this;
    var answers = this.state.answers;
    var isSafety = q.type === 'conditional';
    var isOptional = q.type === 'optional';
    var titleId = this.root.id + '-q-' + q.key;
    var steps = this.coreSteps();
    var eyebrow = isSafety ? 'One more' : 'Question ' + (index + 1) + ' of ' + steps;
    var current = answers[q.key];

    var options = q.answers.filter(function (a) { return !(isOptional && a.value === 'none'); });
    var altTitle = '';
    if (isSafety) {
      var rec = this.engine.recommend(answers, this.config);
      var alt = rec.safetyAlternate && this.config.products[rec.safetyAlternate];
      altTitle = alt ? alt.title : '';
    }

    var group = el('div', {
      class: 'sq-answers ' + (isOptional ? 'sq-answers--chips' : 'sq-answers--cards'),
      role: 'radiogroup',
      'aria-labelledby': titleId,
      'data-sq-group': true
    });
    var hasChecked = options.some(function (a) { return a.value === current; });
    options.forEach(function (a, i) {
      var hint = a.hint || '';
      if (hint.indexOf('{alternate}') !== -1) hint = altTitle ? hint.replace('{alternate}', altTitle) : '';
      var checked = a.value === current;
      var btn = el('button', {
        type: 'button',
        class: 'sq-answer',
        role: 'radio',
        'aria-checked': checked ? 'true' : 'false',
        tabindex: checked || (!hasChecked && i === 0) ? '0' : '-1',
        'data-value': a.value
      }, [
        isOptional || !LETTERS.charAt(i) ? null : el('span', { class: 'sq-answer__key sq-desktop-only', 'aria-hidden': 'true', text: LETTERS.charAt(i) }),
        el('span', { class: 'sq-answer__text' }, [
          el('span', { class: 'sq-answer__label', text: a.label }),
          hint ? el('span', { class: 'sq-answer__hint', text: hint }) : null
        ])
      ]);
      btn.addEventListener('click', function () { self.choose(q, a.value); });
      group.appendChild(btn);
    });

    var body = el('div', { class: 'sq-step__body' });
    // Mask the safety answer from Clarity recordings.
    if (isSafety) body.setAttribute('data-clarity-mask', 'true');

    if (isOptional && answers.concern_1) {
      var first = this.answerLabel('concern_1', answers.concern_1);
      var change = el('button', { type: 'button', class: 'sq-link', text: 'Change' });
      change.setAttribute('aria-label', 'Change your first pick, ' + first);
      change.addEventListener('click', function () { self.jumpTo('concern_1'); });
      body.appendChild(el('div', { class: 'sq-picked' }, [
        el('span', { class: 'sq-picked__label', text: 'You picked first' }),
        el('span', { class: 'sq-picked__row' }, [el('span', { class: 'sq-picked__chip', text: first }), change])
      ]));
    }

    body.appendChild(group);

    if (isOptional) {
      var hasPick = current && current !== 'none';
      var cont = el('button', {
        type: 'button', class: 'sq-cta sq-cta--lg', 'data-sq-continue': true,
        'aria-disabled': hasPick ? null : 'true'
      }, [
        el('span', { text: self.ui.continue || 'Continue' }),
        el('span', { class: 'sq-cta__arrow', 'aria-hidden': 'true', text: '→' })
      ]);
      cont.addEventListener('click', function () {
        var v = self.state.answers.concern_2;
        if (v && v !== 'none') self.next();
      });
      var skip = el('button', { type: 'button', class: 'sq-link', text: 'Skip this one' });
      skip.addEventListener('click', function () {
        self.track('quiz_skip', { step: self.state.index + 1 });
        self.choose(q, 'none');
      });
      body.appendChild(el('div', { class: 'sq-step__actions' }, [cont, skip]));
    }

    if (isSafety && q.note) body.appendChild(el('p', { class: 'sq-step__note', text: q.note }));

    if (!isOptional) {
      var range = 'A–' + LETTERS.charAt(options.length - 1);
      body.appendChild(el('p', { class: 'sq-step__kbd sq-desktop-only', text: 'Press ' + range + ', or click. Picking an answer moves you on.' }));
      body.appendChild(el('p', { class: 'sq-step__kbd sq-mobile-only', text: 'Tap an answer to keep going.' }));
    }

    var title = el('h2', { class: 'sq-step__title', id: titleId, tabindex: '-1', text: q.prompt });
    var head = el('div', { class: 'sq-step__head' }, [
      el('p', { class: 'sq-eyebrow', text: eyebrow }),
      title,
      q.helper ? el('p', { class: 'sq-step__helper', text: q.helper }) : null
    ]);

    this.showStep(el('div', { class: 'sq-step__grid' }, [head, body]));
    this.setProgress(isSafety ? steps : index + 1, steps);
    this.announce(eyebrow);
    title.focus({ preventScroll: true });
  };

  // ---------- result (D5 / M4) ----------

  SkinQuiz.prototype.renderResult = function (r) {
    r = r || this.result();
    var self = this;
    var ui = this.ui;
    var products = this.config.products;
    var count = r.slots.length;

    // Answer chips: tap one to go back to that question.
    var chips = el('ul', { class: 'sq-chips', role: 'list' });
    ['skin_type', 'concern_1', 'concern_2', 'reactivity', 'age_band', 'routine_now'].forEach(function (key) {
      var value = self.state.answers[key];
      if (!value || value === 'none') return;
      var label = self.answerLabel(key, value);
      var chip = el('button', { type: 'button', class: 'sq-chip' }, [
        el('span', { text: label }),
        el('span', { class: 'sq-chip__pencil', 'aria-hidden': 'true', text: '✎' })
      ]);
      chip.setAttribute('aria-label', fill(ui.edit_answer_label, { label: label }));
      chip.addEventListener('click', function () {
        self.track('quiz_edit_answer', { question_key: key });
        self.jumpTo(key);
      });
      chips.appendChild(el('li', null, [chip]));
    });

    var hero = el('section', { class: 'sq-hero' }, [
      el('div', { class: 'sq-hero__inner' }, [
        el('div', { class: 'sq-hero__copy' }, [
          el('p', { class: 'sq-eyebrow', text: ui.result_eyebrow }),
          el('h2', { class: 'sq-hero__name', tabindex: '-1', 'data-sq-result-title': true, text: r.routine.name }),
          el('p', { class: 'sq-hero__summary', text: r.routine.summary }),
          el('p', { class: 'sq-hero__answers-label', text: ui.answers_label }),
          chips,
          r.startHereNote ? el('div', { class: 'sq-starthere' }, [
            el('span', { class: 'sq-badge', text: ui.start_here_badge }),
            el('span', { text: r.startHereNote })
          ]) : null
        ]),
        this.buyBlock(r, 'summary')
      ])
    ]);

    var grid = el('ol', { class: 'sq-steps__grid', role: 'list' });
    r.slots.forEach(function (slot, i) { grid.appendChild(self.stepCard(slot, i, r)); });

    var steps = el('section', { class: 'sq-steps' }, [
      el('div', { class: 'sq-steps__head' }, [
        el('h3', { class: 'sq-steps__title', text: fill(ui.steps_heading, { count: count }) }),
        el('p', { class: 'sq-steps__hint', text: ui.swap_hint })
      ]),
      grid,
      this.undoRow(),
      r.addOns.length ? el('div', { class: 'sq-addons' + (r.addOns.length > 1 ? ' sq-addons--many' : '') },
        r.addOns.map(function (a) { return self.addOnCard(a, r.addOns.length > 1); })) : null
    ]);

    var result = el('div', {
      class: 'sq-result',
      style: r.routine.accentHex ? '--sq-accent: ' + r.routine.accentHex : null
    }, [
      hero,
      steps,
      this.buyBlock(r, 'inline'),
      this.howTo(r),
      this.emailCard(r),
      el('p', { class: 'sq-footnote', text: ui.footnote })
    ]);

    this.showStep(result);
    this.progressEl.hidden = true;
    this.backEl.hidden = true;
    if (this.retakeEl) this.retakeEl.hidden = false;
    if (this.shareEl) this.shareEl.hidden = false;
    this.announce(r.routine.name + '. ' + r.routine.summary);
    this.stickyBar(r);
    var title = result.querySelector('[data-sq-result-title]');
    if (title) title.focus({ preventScroll: true });
  };

  // Re-render in place after a swap, keeping the scroll position.
  SkinQuiz.prototype.refreshResult = function (announce) {
    var y = window.scrollY;
    var r = this.result();
    this.save();
    this.renderResult(r);
    window.scrollTo(0, y);
    if (announce) this.announce(announce);
  };

  SkinQuiz.prototype.stepCard = function (slot, i, r) {
    var self = this;
    var p = this.config.products[slot.handle];
    var ui = this.ui;
    var meta = el('p', { class: 'sq-card__meta' }, [
      el('span', { text: (i + 1) + ' · ' + slot.label }),
      slot.startHere ? el('span', { class: 'sq-badge', text: ui.start_here_badge }) : null
    ]);
    var swap = null;
    if (slot.alternates.length) {
      swap = el('button', { type: 'button', class: 'sq-link', 'data-sq-swap-open': slot.slot, text: ui.swap_label });
      swap.setAttribute('aria-label', fill(ui.swap_title, { noun: slot.noun || slot.label }));
      swap.addEventListener('click', function () { self.openSwap(slot.slot, swap); });
    }
    var remove = null;
    if (r.slots.length > 1) {
      remove = el('button', { type: 'button', class: 'sq-card__remove', 'data-sq-remove': slot.slot }, [
        el('span', { class: 'sq-card__remove-dot', 'aria-hidden': 'true', text: '✕' })
      ]);
      remove.setAttribute('aria-label', fill(ui.remove_label, { title: p.title }));
      remove.addEventListener('click', function () { self.removeSlot(slot.slot); });
    }
    return el('li', { class: 'sq-card' }, [
      remove,
      el('div', { class: 'sq-card__media', style: 'background:' + tint(p.hue, 0.5) }, [
        p.image ? el('img', { src: p.image, alt: '', loading: 'lazy' }) : null
      ]),
      el('div', { class: 'sq-card__body' }, [
        meta,
        el('h4', { class: 'sq-card__title', text: p.title }),
        el('p', { class: 'sq-card__price', text: money(p.price) + ' · ' + slot.when }),
        el('p', { class: 'sq-card__why', text: slot.why }),
        swap
      ])
    ]);
  };

  // Removing a step is reversible and re-prices immediately — including the
  // tier discount, which disappears below 4 items rather than lying.
  SkinQuiz.prototype.removeSlot = function (slotKey) {
    var r = this.result();
    var slot = r.slots.find(function (s) { return s.slot === slotKey; });
    if (!slot || r.slots.length <= 1) return;
    this.state.removed = this.state.removed.concat(slotKey);
    this.track('quiz_remove', { slot: slotKey, product: slot.handle });
    this.lastRemoved = { slot: slotKey, handle: slot.handle };
    this.refreshResult(fill(this.ui.removed_note, { title: this.config.products[slot.handle].title }));
  };

  SkinQuiz.prototype.undoRemove = function () {
    if (!this.lastRemoved) return;
    var slotKey = this.lastRemoved.slot;
    this.state.removed = this.state.removed.filter(function (x) { return x !== slotKey; });
    this.track('quiz_remove_undo', { slot: slotKey, product: this.lastRemoved.handle });
    this.lastRemoved = null;
    this.refreshResult();
  };

  SkinQuiz.prototype.undoRow = function () {
    if (!this.lastRemoved) return null;
    var self = this;
    var title = this.config.products[this.lastRemoved.handle].title;
    var undo = el('button', { type: 'button', class: 'sq-link', text: this.ui.undo });
    undo.addEventListener('click', function () { self.undoRemove(); });
    return el('p', { class: 'sq-undo', role: 'status' }, [
      el('span', { text: fill(this.ui.removed_note, { title: title }) + ' ' }),
      undo
    ]);
  };

  SkinQuiz.prototype.addOnCard = function (addOn, many) {
    var self = this;
    var p = this.config.products[addOn.handle];
    var btn = el('button', { type: 'button', class: 'sq-link', text: 'Add ' + p.title + ' · ' + money(p.price) });
    btn.addEventListener('click', function () { self.addOne(addOn.handle, 'add_on'); });
    return el('div', { class: 'sq-addon' }, [
      el('div', { class: 'sq-addon__media', style: 'background:' + tint(p.hue, 0.5) }, [
        p.image ? el('img', { src: p.image, alt: '', loading: 'lazy' }) : null
      ]),
      el('div', null, [
        el('p', { class: 'sq-card__meta', text: (many && this.ui.add_on_label_many) || this.ui.add_on_label }),
        el('h4', { class: 'sq-card__title', text: p.title }),
        el('p', { class: 'sq-card__why', text: addOn.why }),
        btn
      ])
    ]);
  };

  // One component, rendered twice: the desktop summary card and the mobile
  // block under the steps. CSS shows one or the other.
  SkinQuiz.prototype.buyBlock = function (r, placement) {
    var self = this;
    var ui = this.ui;
    var products = this.config.products;
    var count = r.slots.length;
    var tierPrice = r.tier ? r.tier.price : r.total.price;
    var first = r.slots.find(function (s) { return s.slot === 'treat_am'; }) || r.slots[0];

    var thumbs = el('ul', { class: 'sq-summary__thumbs', role: 'list', 'aria-hidden': 'true' });
    r.slots.forEach(function (s) {
      var p = products[s.handle];
      thumbs.appendChild(el('li', { style: 'background:' + tint(p.hue, 0.5) }, [
        p.image ? el('img', { src: p.image, alt: '', loading: 'lazy' }) : null
      ]));
    });

    var cta = el('button', { type: 'button', class: 'sq-cta sq-cta--split', 'data-sq-add-all': placement }, [
      el('span', { class: 'sq-cta__action', text: fill(ui.add_all, { count: count }) }),
      placement === 'summary' ? el('span', { class: 'sq-cta__price', text: money(tierPrice) }) : null
    ]);
    cta.addEventListener('click', function () { self.addAll(placement); });

    var one = el('button', { type: 'button', class: 'sq-link', text: fill(ui.add_one, { title: products[first.handle].title }) });
    one.addEventListener('click', function () { self.addOne(first.handle, placement); });

    return el('aside', { class: 'sq-buy sq-buy--' + placement }, [
      placement === 'summary' ? el('h3', { class: 'sq-summary__title', text: fill(ui.summary_title, { count: count }) }) : null,
      placement === 'summary' ? thumbs : null,
      el('div', { class: 'sq-buy__price' }, [
        el('span', { class: 'sq-price', text: money(tierPrice) }),
        r.tier ? el('s', { class: 'sq-price__was', text: money(r.total.price) }) : null,
        r.tier ? el('span', { class: 'sq-tierbadge', text: fill(ui.tier_badge, { percent: r.tier.percent, count: count }) }) : null
      ]),
      cta,
      one,
      el('p', { class: 'sq-buy__note', 'data-sq-buy-note': true, text: this.state.added ? ui.added_note : ui.saved_note })
    ]);
  };

  SkinQuiz.prototype.howTo = function (r) {
    var self = this;
    var ui = this.ui;
    var by = {};
    r.slots.forEach(function (s) { by[s.slot] = s; });
    var lists = {
      am: ['cleanse', 'treat_am', 'moisturize', 'spf'],
      pm: ['cleanse', 'treat_pm', 'moisturize']
    };

    function panel(which) {
      var list = el('ol', { class: 'sq-howto__list', role: 'list' });
      lists[which].forEach(function (key) {
        var slot = by[key];
        if (!slot) return;
        var p = self.config.products[slot.handle];
        var note = self.usageNote(slot);
        list.appendChild(el('li', null, [
          el('span', { class: 'sq-howto__n', text: String(list.children.length + 1) }),
          el('span', { class: 'sq-howto__dot', style: 'background:' + (p.hue || 'currentColor'), 'aria-hidden': 'true' }),
          el('span', null, [
            el('span', { class: 'sq-howto__name', text: p.title }),
            note ? el('span', { class: 'sq-howto__note', text: note }) : null
          ])
        ]));
      });
      return el('div', {
        class: 'sq-howto__panel sq-howto__panel--' + which,
        'data-sq-howto-panel': which,
        hidden: which !== self.state.howTo ? true : null
      }, [el('h4', { class: 'sq-howto__label', text: which === 'am' ? ui.morning : ui.night }), list]);
    }

    var toggle = el('div', { class: 'sq-howto__toggle', role: 'tablist' });
    ['am', 'pm'].forEach(function (which) {
      var tab = el('button', {
        type: 'button', class: 'sq-howto__tab', role: 'tab',
        'aria-selected': self.state.howTo === which ? 'true' : 'false',
        'data-sq-howto-tab': which,
        text: which === 'am' ? ui.morning : ui.night
      });
      tab.addEventListener('click', function () {
        self.state.howTo = which;
        self.root.querySelectorAll('[data-sq-howto-tab]').forEach(function (t) {
          t.setAttribute('aria-selected', t.getAttribute('data-sq-howto-tab') === which ? 'true' : 'false');
        });
        self.root.querySelectorAll('[data-sq-howto-panel]').forEach(function (pnl) {
          pnl.hidden = pnl.getAttribute('data-sq-howto-panel') !== which;
        });
      });
      toggle.appendChild(tab);
    });

    return el('section', { class: 'sq-howto' }, [
      el('h3', { class: 'sq-section-title', text: ui.how_to_heading }),
      toggle,
      el('div', { class: 'sq-howto__panels' }, [panel('am'), panel('pm')])
    ]);
  };

  SkinQuiz.prototype.usageNote = function (slot) {
    var notes = this.config.usage_notes || {};
    return notes[slot.slot + '|' + slot.handle] || notes[slot.slot] || '';
  };

  // The email card collects an address and shows the sent state. Nothing is
  // sent yet: Klaviyo is phase 7, and the setting is off by default.
  SkinQuiz.prototype.emailCard = function (r) {
    var self = this;
    var ui = this.ui;
    var pm = r.slots.find(function (s) { return s.slot === 'treat_pm'; });
    var input = el('input', {
      type: 'email', class: 'sq-email__input', id: this.root.id + '-email',
      placeholder: ui.email_placeholder, autocomplete: 'email', 'data-clarity-mask': 'true'
    });
    var label = el('label', { class: 'sq-sr-only', for: this.root.id + '-email', text: ui.email_placeholder });
    var error = el('p', { class: 'sq-email__error', role: 'alert', hidden: true });
    var button = el('button', { type: 'submit', class: 'sq-cta', text: ui.email_button });
    var form = el('form', { class: 'sq-email__form', novalidate: true }, [label, input, button]);
    var card = el('section', { class: 'sq-email' }, [
      el('div', { class: 'sq-email__copy' }, [
        el('h3', { class: 'sq-email__title', text: ui.email_title }),
        el('p', { text: fill(ui.email_body, { treat_pm: pm ? this.config.products[pm.handle].title : 'your routine' }) })
      ]),
      el('div', { class: 'sq-email__field' }, [form, error, el('p', { class: 'sq-email__micro', text: ui.email_micro })])
    ]);

    this.track('quiz_email_view', { routine: r.routine.key });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var value = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        error.textContent = ui.email_invalid;
        error.hidden = false;
        input.focus();
        return;
      }
      self.track('quiz_email_submit', { routine: r.routine.key });
      self.klaviyoIdentify(value, r);
      self.showEmailSent(card, value, r);
    });
    return card;
  };

  SkinQuiz.prototype.showEmailSent = function (card, email, r) {
    var self = this;
    var ui = this.ui;
    var masked = email.replace(/^(.)(.*)(@.*)$/, function (m, a, b, c) { return a + '•••' + c; });
    var cta = el('button', { type: 'button', class: 'sq-cta', text: fill(ui.add_all, { count: r.slots.length }) });
    cta.addEventListener('click', function () { self.addAll('email_confirm'); });
    var sent = el('div', { class: 'sq-email__sent' }, [
      el('h3', { class: 'sq-email__title', tabindex: '-1', text: ui.email_sent_title }),
      el('p', { text: fill(ui.email_sent_body, { email: masked }) }),
      cta,
      this.settings.klaviyo_enabled ? null : el('p', { class: 'sq-email__micro', text: 'Test mode: nothing was sent.' })
    ]);
    card.textContent = '';
    card.classList.add('is-sent');
    card.appendChild(sent);
    sent.querySelector('h3').focus({ preventScroll: true });
  };

  // ---------- Klaviyo (phase 7) ----------
  //
  // Off unless the section setting says otherwise. Three things happen:
  //   completion  -> track "Skin Quiz Completed" (attaches only for profiles
  //                  Klaviyo already knows; anonymous visitors can't be tracked)
  //   email given -> identify with the answers, subscribe to the list, then
  //                  re-track so the event lands on the now-known profile
  // The metric name is deliberately NOT QuizKit's "Quiz Completed": Klaviyo
  // keys metrics by source, so reusing the name would make two metrics with
  // one name and no way to tell them apart in a flow.
  //
  // The pregnancy answer is never included. See profileProps below.

  SkinQuiz.prototype.klaviyoOn = function () {
    return this.settings.klaviyo_enabled === true;
  };

  SkinQuiz.prototype.profileProps = function (r) {
    var a = this.engine.stripPrivate(this.state.answers);
    return {
      quiz_skin_type: a.skin_type,
      quiz_concern_1: a.concern_1,
      quiz_concern_2: a.concern_2 === 'none' ? null : a.concern_2,
      quiz_reactivity: a.reactivity,
      quiz_age_band: a.age_band,
      quiz_routine_now: a.routine_now,
      quiz_routine: r.routine.key,
      quiz_routine_name: r.routine.name,
      quiz_completed_at: new Date().toISOString()
    };
  };

  // What the flow's emails render from. Everything an email needs is in here,
  // so a template loops over `event.products` instead of branching.
  SkinQuiz.prototype.eventProps = function (r) {
    var self = this;
    var products = r.slots.map(function (s, i) {
      var p = self.config.products[s.handle];
      return {
        step: i + 1,
        slot: s.slot,
        label: s.label,
        when: s.when,
        title: p.title,
        handle: s.handle,
        why: s.why,
        price: money(p.price),
        image: p.image,
        url: location.origin + p.url,
        start_here: !!s.startHere
      };
    });
    var pm = r.slots.find(function (s) { return s.slot === 'treat_pm'; });
    var props = this.profileProps(r);
    props.routine = r.routine.key;
    props.routine_name = r.routine.name;
    props.routine_summary = r.routine.summary;
    props.products = products;
    props.product_titles = products.map(function (p) { return p.title; }).join(', ');
    props.product_count = products.length;
    props.total = money(r.total.price);
    props.tier_total = r.tier ? money(r.tier.price) : null;
    props.tier_percent = r.tier ? r.tier.percent : null;
    props.start_here_note = r.startHereNote || null;
    props.result_url = this.shareUrl();
    props.add_all_url = location.origin + '/cart/' + r.slots.map(function (s) {
      var p = self.config.products[s.handle];
      return p.variant_id + ':1';
    }).join(',');
    // Lets the day-3 email split without another template.
    props.has_retinoid = !!(pm && (this.config.products[pm.handle].flags || []).indexOf('retinoid') !== -1);
    props.has_acid = r.slots.some(function (s) {
      return (self.config.products[s.handle].flags || []).indexOf('acid') !== -1;
    });
    props.treat_pm_title = pm ? this.config.products[pm.handle].title : null;
    props.quiz_version = this.config.quiz_version;
    return props;
  };

  // source: 'complete' fires when the result renders, 'email' when they hand
  // over an address. The email one carries any swaps they made in between, so
  // that's the one the flow should trigger on.
  SkinQuiz.prototype.klaviyoTrack = function (r, source) {
    if (!this.klaviyoOn() || !window.klaviyo) return;
    try {
      var props = this.eventProps(r);
      props.source = source || 'complete';
      window.klaviyo.track('Skin Quiz Completed', props);
    } catch (e) {
      console.warn('[skin-quiz] klaviyo track failed', e);
    }
  };

  SkinQuiz.prototype.klaviyoIdentify = function (email, r) {
    if (!this.klaviyoOn()) return Promise.resolve();
    var self = this;
    var props = this.profileProps(r);
    props.$email = email;
    return Promise.resolve()
      .then(function () { return window.klaviyo ? window.klaviyo.identify(props) : null; })
      .then(function () { return self.klaviyoSubscribe(email); })
      .then(function () { self.klaviyoTrack(r, 'email'); })
      .catch(function (err) { console.warn('[skin-quiz] klaviyo identify failed', err); });
  };

  SkinQuiz.prototype.klaviyoSubscribe = function (email) {
    var listId = (this.settings.klaviyo_list_id || '').trim();
    var companyId = (this.settings.klaviyo_company_id || '').trim();
    if (!listId || !companyId) return null; // blank list = don't subscribe
    return fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Skin quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      })
    });
  };

  // ---------- swap (D6 / M6) ----------

  SkinQuiz.prototype.openSwap = function (slotKey, opener) {
    var self = this;
    var ui = this.ui;
    var r = this.result();
    var slot = r.slots.find(function (s) { return s.slot === slotKey; });
    if (!slot || !slot.alternates.length) return;
    var products = this.config.products;
    var index = r.slots.indexOf(slot) + 1;
    var titleId = this.root.id + '-swap-title';
    var chosen = slot.handle;

    var list = el('div', { class: 'sq-swap__list', role: 'radiogroup', 'aria-labelledby': titleId });
    var apply = el('button', { type: 'button', class: 'sq-cta', text: ui.swap_keep });

    [slot.handle].concat(slot.alternates).forEach(function (handle, i) {
      var p = products[handle];
      var diff = p.price - products[slot.handle].price;
      var tag = handle === slot.defaultHandle ? ui.swap_tag_default
        : diff === 0 ? ''
          : fill(diff < 0 ? ui.swap_tag_less : ui.swap_tag_more, { amount: (Math.abs(diff) / 100).toFixed(2).replace(/\.00$/, '') });
      var option = el('button', {
        type: 'button', class: 'sq-swapopt', role: 'radio',
        'aria-checked': handle === chosen ? 'true' : 'false',
        tabindex: handle === chosen ? '0' : '-1',
        'data-handle': handle
      }, [
        el('span', { class: 'sq-swapopt__media', style: 'background:' + tint(p.hue, 0.5) }, [
          p.image ? el('img', { src: p.image, alt: '', loading: 'lazy' }) : null
        ]),
        el('span', { class: 'sq-swapopt__body' }, [
          el('span', { class: 'sq-swapopt__head' }, [
            el('span', { class: 'sq-swapopt__title', text: p.title }),
            el('span', { class: 'sq-swapopt__price', text: money(p.price) })
          ]),
          el('span', { class: 'sq-swapopt__why', text: (self.config.why[slotKey] || {})[handle] || '' }),
          tag ? el('span', { class: 'sq-badge sq-badge--quiet', text: tag }) : null
        ]),
        el('span', { class: 'sq-swapopt__radio', 'aria-hidden': 'true' })
      ]);
      option.addEventListener('click', function () {
        chosen = handle;
        list.querySelectorAll('[role="radio"]').forEach(function (o) {
          var on = o.getAttribute('data-handle') === handle;
          o.setAttribute('aria-checked', on ? 'true' : 'false');
          o.setAttribute('tabindex', on ? '0' : '-1');
        });
        apply.textContent = handle === slot.handle ? ui.swap_keep : fill(ui.swap_use, { title: products[handle].title });
      });
      list.appendChild(option);
    });

    apply.addEventListener('click', function () {
      if (chosen !== slot.handle) {
        self.state.swaps[slotKey] = chosen;
        self.track('quiz_swap', { slot: slotKey, from_product: slot.handle, to_product: chosen });
      }
      self.closeSwap();
      var next = self.result();
      self.refreshResult('Routine updated. ' + money(next.tier ? next.tier.price : next.total.price));
    });

    var panel = el('div', { class: 'sq-swap__panel', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId }, [
      el('div', { class: 'sq-swap__grab', 'aria-hidden': 'true' }),
      el('div', { class: 'sq-swap__head' }, [
        el('p', { class: 'sq-eyebrow', text: 'Step ' + index + ' · ' + slot.label }),
        (function () {
          var x = el('button', { type: 'button', class: 'sq-swap__x', 'aria-label': ui.swap_close, text: '✕' });
          x.addEventListener('click', function () { self.closeSwap(); });
          return x;
        })()
      ]),
      el('h3', { class: 'sq-swap__title', id: titleId, tabindex: '-1', text: fill(ui.swap_title, { noun: slot.noun || slot.label }) }),
      el('p', { class: 'sq-swap__intro', text: (self.config.swap_intro || {})[slotKey] || '' }),
      list,
      el('div', { class: 'sq-swap__foot' }, [apply])
    ]);
    var scrim = el('div', { class: 'sq-swap__scrim' });
    scrim.addEventListener('click', function () { self.closeSwap(); });

    this.closeSwap();
    this.swapEl = el('div', { class: 'sq-swap' }, [scrim, panel]);
    this.swapOpener = opener || null;
    this.root.appendChild(this.swapEl);
    document.body.style.overflow = 'hidden';
    panel.querySelector('.sq-swap__title').focus({ preventScroll: true });
  };

  SkinQuiz.prototype.closeSwap = function () {
    if (!this.swapEl) return;
    this.swapEl.remove();
    this.swapEl = null;
    document.body.style.overflow = '';
    if (this.swapOpener && document.contains(this.swapOpener)) this.swapOpener.focus();
    this.swapOpener = null;
  };

  SkinQuiz.prototype.trapFocus = function (e) {
    if (!this.swapEl || e.key !== 'Tab') return;
    var focusable = this.swapEl.querySelectorAll('button, input, [tabindex="0"]');
    if (!focusable.length) return;
    var firstEl = focusable[0];
    var lastEl = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
    else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
  };

  // ---------- sticky bar (M5) ----------

  SkinQuiz.prototype.stickyBar = function (r) {
    var self = this;
    if (this.stickyObserver) { this.stickyObserver.disconnect(); this.stickyObserver = null; }
    if (this.stickyEl) { this.stickyEl.remove(); this.stickyEl = null; }

    var price = r.tier ? r.tier.price : r.total.price;
    var cta = el('button', { type: 'button', class: 'sq-cta', text: fill(this.ui.add_all, { count: r.slots.length }) });
    cta.addEventListener('click', function () { self.addAll('sticky'); });
    this.stickyEl = el('div', { class: 'sq-sticky', hidden: true, 'aria-hidden': 'true' }, [
      el('div', { class: 'sq-sticky__price' }, [
        el('span', { class: 'sq-price', text: money(price) }),
        r.tier ? el('s', { class: 'sq-price__was', text: money(r.total.price) }) : null
      ]),
      cta
    ]);
    this.root.appendChild(this.stickyEl);

    var inline = this.root.querySelector('.sq-buy--inline');
    if (!inline || !window.IntersectionObserver) return;
    this.stickyObserver = new IntersectionObserver(function (entries) {
      var showing = entries[0].isIntersecting;
      self.stickyEl.hidden = showing;
      self.stickyEl.setAttribute('aria-hidden', showing ? 'true' : 'false');
    }, { rootMargin: '0px 0px -40px 0px' });
    this.stickyObserver.observe(inline);
  };

  // ---------- cart (phase 4) ----------

  SkinQuiz.prototype.sid = function () {
    try {
      var id = sessionStorage.getItem('sq:sid');
      if (!id) {
        id = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2);
        sessionStorage.setItem('sq:sid', id);
      }
      return id;
    } catch (e) {
      return 'no-storage';
    }
  };

  SkinQuiz.prototype.lineProperties = function (routineKey, groupId) {
    var props = { _source: 'quiz', _quiz_routine: routineKey, _quiz_session: this.sid() };
    // Same group id on every line: the discount function prices a
    // "cart-builder-*" group from the cart_tiers metafield.
    if (groupId) props._bundle_id = groupId;
    return props;
  };

  SkinQuiz.prototype.addAll = function (placement) {
    var self = this;
    var r = this.result();
    var group = 'cart-builder-' + Date.now();
    var items = [];
    r.slots.forEach(function (s) {
      var p = self.config.products[s.handle];
      if (p && p.variant_id) items.push({ id: p.variant_id, quantity: 1, properties: self.lineProperties(r.routine.key, group) });
    });
    if (!items.length) return;
    this.track('quiz_add_all', {
      routine: r.routine.key, value: (r.tier ? r.tier.price : r.total.price) / 100,
      items: items.length, placement: placement
    });
    this.addToCart(items, r, placement);
  };

  SkinQuiz.prototype.addOne = function (handle, placement) {
    var r = this.result();
    var p = this.config.products[handle];
    if (!p || !p.variant_id) return;
    var slot = r.slots.find(function (s) { return s.handle === handle; });
    this.track('quiz_add_one', { slot: slot ? slot.slot : 'add_on', product: handle });
    this.addToCart([{ id: p.variant_id, quantity: 1, properties: this.lineProperties(r.routine.key, null) }], r, placement);
  };

  SkinQuiz.prototype.addToCart = function (items, r, placement) {
    var self = this;
    var skus = items.map(function (i) {
      var handle = self.handleForVariant(i.id);
      return handle ? self.config.products[handle].sku : '';
    }).filter(Boolean);
    var ids = items.map(function (i) { return String(i.id); });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add ' + res.status);
        return self.updateCartAttributes({
          quiz_session: self.sid(),
          quiz_routine: r.routine.key,
          quiz_added: ids,
          quiz_added_skus: skus
        });
      })
      .then(function () {
        self.state.added = true;
        self.setBuyNote(self.ui.added_note, true);
        // The cart drawer listens for this; it owns opening and rendering.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        if (window.theme && window.theme.drawer && typeof window.theme.drawer.open === 'function') {
          try { window.theme.drawer.open('right', 'cart-drawer'); } catch (e) { /* drawer API changed; the link is the fallback */ }
        }
      })
      .catch(function (err) {
        console.error('[skin-quiz] add to cart failed', err);
        self.setBuyNote('That didn’t go through. Try again.', false);
      });
  };

  SkinQuiz.prototype.handleForVariant = function (id) {
    var products = this.config.products;
    for (var h in products) if (products[h] && products[h].variant_id === id) return h;
    return null;
  };

  // Cart attributes are what reach the Shopify order export (line properties
  // don't), so quiz attribution rides on these. Merge, never clobber.
  SkinQuiz.prototype.updateCartAttributes = function (attrs) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : { attributes: {} }; })
      .catch(function () { return { attributes: {} }; })
      .then(function (cart) {
        var existing = cart.attributes || {};
        var merged = {};
        for (var k in attrs) {
          var v = attrs[k];
          if (Array.isArray(v)) {
            var prev = (existing[k] || '').split(',').filter(Boolean);
            v.forEach(function (item) { if (prev.indexOf(String(item)) === -1) prev.push(String(item)); });
            merged[k] = prev.join(',');
          } else {
            merged[k] = v;
          }
        }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: merged })
        });
      })
      .catch(function (err) { console.warn('[skin-quiz] cart attributes not set', err); });
  };

  SkinQuiz.prototype.setBuyNote = function (text, showLink) {
    var self = this;
    this.root.querySelectorAll('[data-sq-buy-note]').forEach(function (note) {
      note.textContent = text + ' ';
      if (showLink) {
        var link = el('a', { class: 'sq-link', href: '/cart', text: self.ui.view_bag });
        note.appendChild(link);
      }
    });
  };

  // ---------- resume + share ----------

  SkinQuiz.prototype.saveable = function () {
    return {
      v: 1,
      a: this.engine.stripPrivate(this.state.answers),
      s: this.state.swaps,
      d: this.state.removed,
      x: this.result().safetySwap ? 1 : 0
    };
  };

  SkinQuiz.prototype.save = function () {
    try { localStorage.setItem('sq:v1', JSON.stringify(this.saveable())); } catch (e) { /* private mode */ }
  };

  SkinQuiz.prototype.load = function () {
    try {
      var raw = localStorage.getItem('sq:v1');
      var data = raw ? JSON.parse(raw) : null;
      return data && data.v === 1 && data.a ? data : null;
    } catch (e) {
      return null;
    }
  };

  SkinQuiz.prototype.clearSaved = function () {
    try { localStorage.removeItem('sq:v1'); } catch (e) { /* private mode */ }
  };

  // Rebuild state from a saved or shared payload. The pregnancy answer is never
  // in there: only the swap it caused, replayed as "unsure" in memory.
  SkinQuiz.prototype.restore = function (data) {
    var answers = {};
    var keys = ['concern_1', 'concern_2', 'skin_type', 'reactivity', 'age_band', 'routine_now'];
    for (var i = 0; i < keys.length; i++) {
      var v = (data.a || {})[keys[i]];
      // Not every path answers every question — "Nothing in particular" skips
      // Q2 — so take what's there and let isComplete judge the set.
      if (typeof v === 'string') answers[keys[i]] = v;
    }
    if (!answers.concern_1) return false;
    if (data.x) answers.pregnant = 'unsure';
    if (!this.engine.isComplete(answers, this.config)) {
      // Needs the safety answer and the payload didn't record a swap: it was "no".
      answers.pregnant = 'no';
      if (!this.engine.isComplete(answers, this.config)) return false;
    }
    this.state.answers = answers;
    this.state.swaps = {};
    var swaps = data.s || {};
    for (var slot in swaps) if (typeof swaps[slot] === 'string') this.state.swaps[slot] = swaps[slot];
    this.state.removed = (Array.isArray(data.d) ? data.d : []).filter(function (x) {
      return typeof x === 'string' && SLOTS.indexOf(x) !== -1;
    });
    return true;
  };

  SkinQuiz.prototype.readShareUrl = function () {
    try {
      var raw = new URLSearchParams(location.search).get('r');
      if (!raw) return null;
      var json = atob(raw.replace(/-/g, '+').replace(/_/g, '/'));
      var data = JSON.parse(json);
      return data && data.v === 1 && data.a ? data : null;
    } catch (e) {
      return null;
    }
  };

  SkinQuiz.prototype.shareUrl = function () {
    var payload = btoa(JSON.stringify(this.saveable())).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return location.origin + location.pathname + '?r=' + payload;
  };

  SkinQuiz.prototype.clearShareUrl = function () {
    if (location.search) history.replaceState(null, '', location.origin + location.pathname);
  };

  SkinQuiz.prototype.share = function () {
    var self = this;
    var url = this.shareUrl();
    this.track('quiz_share', {});
    function copied() {
      var was = self.shareEl.textContent;
      self.shareEl.textContent = self.ui.share_copied;
      self.announce(self.ui.share_copied);
      setTimeout(function () { self.shareEl.textContent = was; }, 2000);
    }
    if (navigator.share) {
      navigator.share({ url: url, title: 'My Mad Hippie routine' }).catch(function () {});
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(copied, function () { prompt('Copy your link', url); });
      return;
    }
    prompt('Copy your link', url);
  };

  SkinQuiz.prototype.answerLabel = function (key, value) {
    var qs = this.config.questions;
    for (var i = 0; i < qs.length; i++) {
      if (qs[i].key !== key) continue;
      for (var j = 0; j < qs[i].answers.length; j++) if (qs[i].answers[j].value === value) return qs[i].answers[j].label;
    }
    return value;
  };

  SkinQuiz.prototype.syncChecked = function (value) {
    var radios = this.stepEl.querySelectorAll('[role="radio"]');
    for (var i = 0; i < radios.length; i++) {
      var on = radios[i].getAttribute('data-value') === value;
      radios[i].setAttribute('aria-checked', on ? 'true' : 'false');
      radios[i].setAttribute('tabindex', on ? '0' : '-1');
    }
    if (!this.stepEl.querySelector('[role="radio"][tabindex="0"]') && radios[0]) radios[0].setAttribute('tabindex', '0');
  };

  // Segments are rendered server-side for the common 6-question path; a path
  // that skips one (Q1 = "Nothing in particular") re-renders them.
  SkinQuiz.prototype.coreSteps = function () {
    return this.flow().filter(function (q) { return q.type !== 'conditional'; }).length;
  };

  SkinQuiz.prototype.setProgress = function (filled, total) {
    this.progressEl.hidden = false;
    total = total || this.coreSteps();
    if (this.progressEl.children.length !== total) {
      this.progressEl.textContent = '';
      for (var n = 0; n < total; n++) this.progressEl.appendChild(el('span', { class: 'sq-progress__seg' }));
      this.progressEl.style.gridTemplateColumns = 'repeat(' + total + ', 1fr)';
    }
    var segs = this.progressEl.children;
    for (var i = 0; i < segs.length; i++) segs[i].classList.toggle('is-done', i < filled);
  };

  SkinQuiz.prototype.announce = function (text) {
    if (this.statusEl) this.statusEl.textContent = text;
  };

  SkinQuiz.prototype.scrollToTop = function () {
    var top = this.root.getBoundingClientRect().top;
    if (top < 0) this.root.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  // ---------- keyboard ----------

  SkinQuiz.prototype.onKeydown = function (e) {
    if (e.defaultPrevented) return;
    if (this.swapEl) {
      if (e.key === 'Escape') { e.preventDefault(); this.closeSwap(); return; }
      this.trapFocus(e);
      return;
    }
    if (this.state.screen !== 'question') return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    var t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    // Ignore keys aimed at the rest of the page (header search, drawers).
    if (t && t !== document.body && !this.root.contains(t)) return;

    var radios = Array.prototype.slice.call(this.stepEl.querySelectorAll('[role="radio"]'));
    var inGroup = radios.indexOf(t) !== -1;
    var key = e.key;

    if (inGroup && (key === 'ArrowDown' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowLeft')) {
      e.preventDefault();
      var dir = key === 'ArrowDown' || key === 'ArrowRight' ? 1 : -1;
      var nextIdx = (radios.indexOf(t) + dir + radios.length) % radios.length;
      radios.forEach(function (r, i) { r.setAttribute('tabindex', i === nextIdx ? '0' : '-1'); });
      radios[nextIdx].focus();
      return;
    }

    if (key === 'Backspace' || (key === 'ArrowLeft' && !inGroup)) {
      e.preventDefault();
      this.back();
      return;
    }

    if (key.length === 1) {
      var idx = LETTERS.indexOf(key.toUpperCase());
      if (idx !== -1 && idx < radios.length) {
        e.preventDefault();
        radios[idx].focus();
        radios[idx].click();
      }
    }
  };

  onReady(function () {
    var roots = document.querySelectorAll('[data-sq-root]');
    for (var i = 0; i < roots.length; i++) {
      var quiz = new SkinQuiz(roots[i]);
      roots[i].skinQuiz = quiz;
      // Documented entry point: window.SkinQuiz.track(name, props)
      if (!window.SkinQuiz) window.SkinQuiz = quiz;
      quiz.init();
    }
  });
})();
